import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, MessageCircle, Loader2, ArrowLeft, Gift, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';
import { getContacts, getThread, sendMessage, pingTyping } from '../api/messages';
import { getBlockStatus, unblockUser } from '../api/blocks';
import { markNotificationsRead, listNotifications } from '../api/notifications';
import ReadOnlyNotice from '../components/ReadOnlyNotice';
import { canWrite } from '../lib/canWrite';
import SharedPostPreview from '../components/SharedPostPreview';
import ContactsPane, { PresenceLed, VoceProBadge } from '../components/messages/ContactsPane';
import ChatHeaderMenu from '../components/messages/ChatHeaderMenu';
import ChatTipDialog from '../components/messages/ChatTipDialog';
import { fmtCredits } from '../lib/currency';

// Phase 50 · Regex to detect and strip the `[nymia:post:<uuid>]` share token.
const SHARED_POST_TOKEN = /\[nymia:post:([a-zA-Z0-9-]+)\]/;

/**
 * MessagesPage — Phase F4 Voice-First rework.
 *   Left: A. conversations (last message first) + B. ♡ Preferiti without a
 *         conversation yet (presence LED + Voce Pro badge, no duplicates).
 *   Right: EXISTING thread (SSE, typing, shared posts preserved) extended
 *          with ⋯ menu (Segnala / Blocca / Elimina), blocked banner and
 *          🎁 Tip (Voce Pro recipients only).
 */
const MessagesPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('messages');
  const [params, setParams] = useSearchParams();
  const activeUsername = params.get('u') || '';
  const [contacts, setContacts] = useState({ conversations: [], favorites: [] });
  const [thread, setThread] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [blockStatus, setBlockStatus] = useState({ i_blocked_them: false, they_blocked_me: false });
  const [loadingConv, setLoadingConv] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const endRef = useRef(null);
  const lastTypingRef = useRef(0);

  const loadContacts = async () => {
    setLoadingConv(true);
    try {
      const data = await getContacts();
      setContacts({
        conversations: data.conversations || [],
        favorites: data.favorites || [],
      });
      return data;
    } catch (_e) { return null; }
    finally { setLoadingConv(false); }
  };

  const resolveOther = (data, username) => {
    const fromConv = (data?.conversations || []).find((c) => c.username === username);
    if (fromConv) return fromConv;
    const fromFav = (data?.favorites || []).find((f) => f.username === username);
    if (fromFav) return { ...fromFav, last_message: '', unread_count: 0 };
    return null;
  };

  const loadThread = async (username) => {
    setLoadingThread(true);
    try {
      const msgs = await getThread(username);
      setThread(msgs);
      const data = await loadContacts();
      let other = resolveOther(data, username);
      if (!other) {
        // Deep-link (?u=) toward someone outside the contacts list — public
        // profile lookup ONLY (@username, avatar, Voce Pro concept).
        try {
          const res = await api.get(`/users/${encodeURIComponent(username)}`);
          other = {
            user_id: res.data.id,
            username: res.data.username,
            avatar_url: res.data.avatar_url || '',
            is_voce_pro: !!res.data.is_verified_creator,
            is_online: undefined,
          };
        } catch (_e) {
          other = { username, avatar_url: '', is_voce_pro: false, is_online: undefined };
        }
      }
      setOtherUser(other);
      setContacts((cur) => ({
        ...cur,
        conversations: cur.conversations.map((x) =>
          x.username === username ? { ...x, unread_count: 0 } : x),
      }));
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 404) {
        toast({ title: t('errors.user_not_found'), variant: 'destructive' });
      } else {
        toast({ title: t('errors.load_thread_error'), description: detail, variant: 'destructive' });
      }
    } finally { setLoadingThread(false); }
  };

  const loadBlockStatus = async (username) => {
    try {
      const st = await getBlockStatus(username);
      setBlockStatus(st || { i_blocked_them: false, they_blocked_me: false });
    } catch (_e) {
      setBlockStatus({ i_blocked_them: false, they_blocked_me: false });
    }
  };

  useEffect(() => { loadContacts(); }, []);

  useEffect(() => {
    if (activeUsername) {
      loadThread(activeUsername);
      loadBlockStatus(activeUsername);
    } else {
      setOtherUser(null);
      setBlockStatus({ i_blocked_them: false, they_blocked_me: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUsername]);

  // Phase 18.1 — real-time SSE stream for the active thread (unchanged).
  useEffect(() => {
    if (!activeUsername) return undefined;
    const token = localStorage.getItem('ig_token');
    if (!token) return undefined;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/messages/with/${encodeURIComponent(activeUsername)}/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch (_e) {
      return undefined;
    }
    es.addEventListener('message', (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        setThread((cur) => {
          if (cur.some((m) => m.id === msg.id)) return cur;
          return [...cur, msg];
        });
        if (msg.recipient_id === user?.id) {
          (async () => {
            try {
              const nl = await listNotifications({ limit: 10, unreadOnly: true });
              const related = nl
                .filter((n) => n.kind === 'new_message' && n.actor?.username === activeUsername)
                .map((n) => n.id);
              if (related.length) await markNotificationsRead(related);
            } catch (_e) { /* ignore */ }
          })();
          loadContacts();
        }
      } catch (_e) { /* ignore malformed events */ }
    });
    es.addEventListener('typing', (evt) => {
      try {
        const data = JSON.parse(evt.data);
        setOtherTyping(!!data.typing);
      } catch (_e) { /* ignore */ }
    });
    es.onerror = () => {};
    return () => { try { es.close(); } catch (_e) { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUsername, user?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!canWrite(user)) {
      toast({ title: t('errors.verify_email_title'), description: t('errors.verify_email_desc'), variant: 'destructive' });
      return;
    }
    const body = draft.trim();
    if (!body || !activeUsername) return;
    setSending(true);
    try {
      const msg = await sendMessage(activeUsername, body);
      setThread((th) => [...th, msg]);
      setDraft('');
      loadContacts();
    } catch (err) {
      toast({
        title: t('errors.send_failed_title'),
        description: String(err.response?.data?.detail || '').split('|').pop() || t('errors.send_failed_default'),
        variant: 'destructive',
      });
    } finally { setSending(false); }
  };

  const handleUnblock = async () => {
    try {
      await unblockUser(activeUsername);
      toast({ title: t('blocked.unblock_success') });
      loadBlockStatus(activeUsername);
      loadContacts();
    } catch (_e) {
      toast({ title: t('blocked.action_failed'), variant: 'destructive' });
    }
  };

  if (!user) return null;

  const isBlocked = blockStatus.i_blocked_them || blockStatus.they_blocked_me;
  const canTip = !!otherUser?.is_voce_pro && !isBlocked && activeUsername !== user.username;

  return (
    <div className="mx-auto flex h-[calc(100vh-3.5rem)] max-w-5xl gap-4 px-2 py-3 md:py-6" data-testid="messages-page">
      {/* Left pane — Phase F4: conversations + ♡ Preferiti */}
      <aside
        className={`flex w-full flex-col rounded-2xl border border-border bg-card md:w-[320px] ${
          activeUsername ? 'hidden md:flex' : 'flex'
        }`}
        data-testid="conversations-pane"
      >
        <ContactsPane
          conversations={contacts.conversations}
          favorites={contacts.favorites}
          activeUsername={activeUsername}
          onSelect={(u) => setParams({ u })}
          loading={loadingConv}
          t={t}
          locale={i18n.language || 'it'}
        />
      </aside>

      {/* Right pane — thread */}
      <section
        className={`flex flex-1 flex-col rounded-2xl border border-border bg-card ${
          activeUsername ? 'flex' : 'hidden md:flex'
        }`}
        data-testid="thread-pane"
      >
        {activeUsername ? (
          <>
            <header className="flex items-center gap-2 border-b border-border px-3 py-3 md:px-4">
              <button
                onClick={() => setParams({})}
                className="md:hidden rounded-md p-1.5 hover:bg-secondary"
                aria-label={t('back_aria')}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              {otherUser && (
                <button
                  onClick={() => navigate(`/u/${activeUsername}`)}
                  className="flex min-w-0 items-center gap-2 hover:opacity-80"
                  data-testid="thread-other-link"
                >
                  <div className="relative shrink-0">
                    <Avatar src={otherUser.avatar_url} name={otherUser.username} size={36} />
                    {otherUser.is_online !== undefined && (
                      <PresenceLed online={otherUser.is_online} username={otherUser.username} prefix="thread" />
                    )}
                  </div>
                  <span className="flex min-w-0 items-center gap-1.5">
                    <p className="truncate text-sm font-semibold">@{otherUser.username}</p>
                    {otherUser.is_voce_pro && <VoceProBadge username={otherUser.username} label={t('voce_pro')} />}
                  </span>
                </button>
              )}
              <div className="ml-auto flex items-center gap-1">
                {canTip && canWrite(user) && (
                  <button
                    onClick={() => setTipOpen(true)}
                    className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-3.5 py-2 gold-text transition-colors hover:bg-[#D4AF37]/20"
                    aria-label={t('tip.btn_aria')}
                    title={t('tip.btn_aria')}
                    data-testid="chat-tip-btn"
                  >
                    <Gift className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.75} />
                    <span className="text-sm font-bold uppercase tracking-wide" data-testid="chat-tip-label">{t('tip.btn_label')}</span>
                  </button>
                )}
                <ChatHeaderMenu
                  username={activeUsername}
                  iBlockedThem={blockStatus.i_blocked_them}
                  onBlockChanged={() => { loadBlockStatus(activeUsername); loadContacts(); }}
                  onDeleted={() => { setParams({}); loadContacts(); }}
                  t={t}
                />
              </div>
            </header>
            <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="thread-messages">
              {loadingThread && thread.length === 0 ? (
                <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : thread.length === 0 ? (
                <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                  {t('empty_thread')}
                </div>
              ) : thread.map((m) => {
                const mine = m.sender_id === user.id;
                // Phase F4 — confirmed 🎁 Tip system event (centered pill).
                if (m.kind === 'tip') {
                  return (
                    <div key={m.id} className="mb-2 flex justify-center" data-testid={`msg-tip-${m.id}`}>
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold gold-text">
                        {mine
                          ? t('tip.sent_label', { amount: fmtCredits(m.tip_amount || 0), username: activeUsername })
                          : t('tip.received_label', { amount: fmtCredits(m.tip_amount || 0), username: activeUsername })}
                      </span>
                    </div>
                  );
                }
                const tokenMatch = (m.body || '').match(SHARED_POST_TOKEN);
                const sharedId = m.shared_post_id || (tokenMatch ? tokenMatch[1] : null);
                const cleanBody = tokenMatch ? m.body.replace(SHARED_POST_TOKEN, '').trim() : m.body;
                return (
                  <div
                    key={m.id}
                    className={`mb-1.5 flex ${mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`msg-${m.id}`}
                  >
                    <div
                      className={`max-w-[78%] rounded-2xl px-3 py-1.5 text-sm whitespace-pre-wrap break-words ${
                        mine
                          ? 'bg-amber-500 text-black rounded-br-sm'
                          : 'bg-secondary text-foreground rounded-bl-sm'
                      }`}
                    >
                      {cleanBody && <div>{cleanBody}</div>}
                      {sharedId && <SharedPostPreview postId={sharedId} mine={mine} />}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
              {otherTyping && otherUser && !isBlocked && (
                <div className="mb-1.5 flex justify-start" data-testid="typing-indicator">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-secondary px-3 py-2 text-xs text-muted-foreground">
                    <span className="gold-text">@{otherUser.username}</span>
                    <span className="typing-dots">
                      <span /><span /><span />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {isBlocked ? (
              /* Phase F4 — blocked state banner replaces the composer */
              <div
                className="flex flex-col items-center gap-2 border-t border-border px-4 py-4 text-center"
                data-testid="chat-blocked-banner"
              >
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Ban className="h-4 w-4" />
                  {blockStatus.i_blocked_them
                    ? t('blocked.you_blocked', { username: activeUsername })
                    : t('blocked.blocked_you')}
                </p>
                {blockStatus.i_blocked_them && (
                  <Button size="sm" variant="outline" onClick={handleUnblock} data-testid="chat-unblock-btn">
                    {t('blocked.unblock_btn')}
                  </Button>
                )}
              </div>
            ) : (
              <form
                onSubmit={send}
                className="flex flex-col gap-2 border-t border-border px-3 py-2.5"
                data-testid="thread-form"
              >
                <ReadOnlyNotice action={t('read_only_action')} />
                <div className="flex items-center gap-2">
                  <Input
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      if (!activeUsername || !canWrite(user)) return;
                      const now = Date.now();
                      if (now - lastTypingRef.current > 3000) {
                        lastTypingRef.current = now;
                        pingTyping(activeUsername).catch(() => {});
                      }
                    }}
                    placeholder={canWrite(user) ? t('input_placeholder_write') : t('input_placeholder_verify')}
                    maxLength={2000}
                    disabled={!canWrite(user)}
                    data-testid="thread-input"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !draft.trim() || !canWrite(user)}
                    className="gold-gradient text-black"
                    data-testid="thread-send-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </form>
            )}
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
            <MessageCircle className="h-10 w-10 opacity-40" />
            <p className="text-sm">{t('select_prompt')}</p>
          </div>
        )}
      </section>

      {/* Phase F4 — 🎁 Tip (existing Tip/Credits system, Voce Pro only) */}
      <ChatTipDialog
        open={tipOpen}
        onOpenChange={setTipOpen}
        username={activeUsername}
        t={t}
        onSent={(sysMsg) => {
          if (sysMsg) setThread((th) => (th.some((m) => m.id === sysMsg.id) ? th : [...th, sysMsg]));
          loadContacts();
        }}
      />
    </div>
  );
};

export default MessagesPage;
